import React, { useState, useEffect } from "react"
import {
  Popover,
  Form,
  Select,
  Button,
  Typography,
  message,
  Spin,
  Tabs,
  List,
  Input,
  Space,
  Popconfirm,
} from "antd"
import {
  doc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc as firestoreUpdate,
} from "firebase/firestore"
import { db } from "../../../firebase/config"

const { Link, Text } = Typography
const { Option } = Select
const { TabPane } = Tabs

export default function PalletEditPopover({ record }) {
  // Popover open state
  const [open, setOpen] = useState(false)

  // Selection state
  const [newPallet, setNewPallet] = useState(record.palletLocation || "")
  const [saving, setSaving] = useState(false)

  // Locations & loading
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(false)

  // CRUD form state
  const [newLocName, setNewLocName] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [editingName, setEditingName] = useState("")
  const [crudLoading, setCrudLoading] = useState(false)

  // Load all locations whenever the popover opens
  useEffect(() => {
    if (!open) return
    ;(async () => {
      setLoading(true)
      try {
        const snap = await getDocs(collection(db, "palletLocations"))
        setLocations(snap.docs.map((d) => ({ id: d.id, name: d.data().name })))
      } catch (err) {
        console.error(err)
        message.error("Unable to load pallet locations.")
      } finally {
        setLoading(false)
      }
    })()
  }, [open])

  // Save selection into rollStore
  const handleSaveSelection = async () => {
    if (!newPallet) {
      message.error("Please select a pallet location.")
      return
    }
    setSaving(true)
    try {
      const rollRef = doc(db, "rollStore", record.id)
      await updateDoc(rollRef, {
        palletLocation: newPallet,
        updated: serverTimestamp(),
      })
      message.success("Pallet updated!")
      setOpen(false)
    } catch (err) {
      console.error(err)
      message.error("Save failed.")
    } finally {
      setSaving(false)
    }
  }

  // --- CRUD Handlers (inline) ---
  const handleAdd = async () => {
    if (!newLocName.trim()) {
      message.error("Name cannot be empty.")
      return
    }
    setCrudLoading(true)
    try {
      await addDoc(collection(db, "palletLocations"), {
        name: newLocName.trim(),
      })
      message.success("Location added")
      setNewLocName("")
      // reload
      const snap = await getDocs(collection(db, "palletLocations"))
      setLocations(snap.docs.map((d) => ({ id: d.id, name: d.data().name })))
    } catch {
      message.error("Add failed")
    } finally {
      setCrudLoading(false)
    }
  }

  const startEdit = (loc) => {
    setEditingId(loc.id)
    setEditingName(loc.name)
  }
  const cancelEdit = () => {
    setEditingId(null)
    setEditingName("")
  }
  const saveEdit = async () => {
    if (!editingName.trim()) {
      message.error("Name cannot be empty.")
      return
    }
    setCrudLoading(true)
    try {
      const locRef = doc(db, "palletLocations", editingId)
      await firestoreUpdate(locRef, { name: editingName.trim() })
      message.success("Updated")
      cancelEdit()
      const snap = await getDocs(collection(db, "palletLocations"))
      setLocations(snap.docs.map((d) => ({ id: d.id, name: d.data().name })))
    } catch {
      message.error("Update failed")
    } finally {
      setCrudLoading(false)
    }
  }

  const handleDelete = async (id) => {
    setCrudLoading(true)
    try {
      await deleteDoc(doc(db, "palletLocations", id))
      message.success("Deleted")
      const snap = await getDocs(collection(db, "palletLocations"))
      setLocations(snap.docs.map((d) => ({ id: d.id, name: d.data().name })))
    } catch {
      message.error("Delete failed")
    } finally {
      setCrudLoading(false)
    }
  }

  // Combined popover content
  const popoverContent = (
    <div style={{ width: 360, maxHeight: 400, overflowY: "auto" }}>
      {loading ? (
        <Spin tip="Loading…" />
      ) : (
        <Tabs defaultActiveKey="1">
          {/* Tab 1: Select */}
          <TabPane tab="Select" key="1">
            <Form layout="vertical" onFinish={handleSaveSelection}>
              <Form.Item label="Pallet Location" required>
                <Select
                  showSearch
                  placeholder="Select pallet…"
                  optionFilterProp="children"
                  value={newPallet}
                  onChange={setNewPallet}
                  filterOption={(inp, opt) =>
                    opt.children.toLowerCase().includes(inp.toLowerCase())
                  }
                  style={{ width: "100%" }}>
                  {locations.map((loc) => (
                    <Option key={loc.id} value={loc.name}>
                      {loc.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" size="small" loading={saving}>
                    Save
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>

          {/* Tab 2: Manage */}
          <TabPane tab="Manage" key="2">
            <Space style={{ marginBottom: 16 }}>
              <Input
                placeholder="New location"
                value={newLocName}
                onChange={(e) => setNewLocName(e.target.value)}
              />
              <Button type="primary" onClick={handleAdd} loading={crudLoading}>
                Add
              </Button>
            </Space>
            <List
              bordered
              dataSource={locations}
              loading={crudLoading}
              renderItem={(loc) => (
                <List.Item
                  actions={
                    editingId === loc.id
                      ? [
                          <Button key="save" type="link" onClick={saveEdit}>
                            Save
                          </Button>,
                          <Button key="cancel" onClick={cancelEdit}>
                            Cancel
                          </Button>,
                        ]
                      : [
                          <Button key="edit" type="link" onClick={() => startEdit(loc)}>
                            Edit
                          </Button>,
                          <Popconfirm
                            key="del"
                            title="Delete this location?"
                            onConfirm={() => handleDelete(loc.id)}
                            okText="Yes"
                            cancelText="No">
                            <Button type="link" danger>
                              Delete
                            </Button>
                          </Popconfirm>,
                        ]
                  }>
                  {editingId === loc.id ? (
                    <Input
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      size="small"
                    />
                  ) : (
                    <Text>{loc.name}</Text>
                  )}
                </List.Item>
              )}
              style={{ maxHeight: 220, overflowY: "auto" }}
            />
          </TabPane>
        </Tabs>
      )}
    </div>
  )

  return (
    <Popover
      content={popoverContent}
      title="Pallet Management"
      trigger="click"
      placement="right"
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen)
        if (isOpen) {
          setNewPallet(record.palletLocation || "")
        }
      }}
      overlayStyle={{}}
      overlayInnerStyle={{ padding: 16 }}>
      <Link style={{ cursor: "pointer", textAlign: "center", display: "block" }}>
        {record.palletLocation || <em>(none)</em>}
      </Link>
    </Popover>
  )
}
